import { useEffect, useState } from 'react'
import { apiGet } from '../../generic/Api_Functions'

/**
 * Index every version born while the index dimension is off (RN-G20: with USE_WORKFLOW_INDEX off
 * everything is sealed APPROVED, so the user is never asked). Same literal the detail's importer
 * already uses (`IMPORTED_VERSION_INDEX_VALUE`).
 */
export const DEFAULT_INDEX_VALUE_WITHOUT_INDEX = 'APPROVED'

/**
 * useBomLibraryIndexSetting — is the index dimension active? (PO adjustment 2026-09-10).
 *
 * The BOM library honours the SAME setting as the workflow editor, read the same way
 * (`workflow2.0/Workflow.jsx`: `apiGet('settings/USE_WORKFLOW_INDEX')` +
 * `value?.toLowerCase() === 'true'`); there is no dedicated endpoint and no new contract with Sun.
 *
 * - `useIndex = true` ⇒ everything behaves as it did until today (index column, index selectors).
 * - `useIndex = false` ⇒ no index value is shown anywhere in the library and every new version is
 *   born APPROVED without asking (RN-G20: with the flag off the workflow is always sealed APPROVED).
 *
 * While the answer is in flight `useIndex` is `true` (today's behaviour, so nothing flickers into a
 * more permissive state) and `loaded` is `false` for the callers that must wait before acting.
 * A setting that cannot be read also keeps `useIndex = true`.
 *
 * Each component that needs the flag calls the hook: the library screen therefore reads the setting
 * once per mounted component (list + detail + save/import dialogs, all of them short-lived). The
 * duplication is accepted on purpose — threading a prop through the dialogs would not let them be
 * mounted (or tested) standalone, and the deep-link route `/resources/bomLibrary/:code` mounts the
 * detail without any list above it.
 *
 * @returns {{useIndex: boolean, loaded: boolean}}
 */
const useBomLibraryIndexSetting = () => {
  const [useIndex, setUseIndex] = useState(true)
  const [loaded, setLoaded] = useState(false)

  useEffect(() => {
    let active = true
    apiGet('settings/USE_WORKFLOW_INDEX')
      .then((setting) => {
        if (!active) return
        setUseIndex(setting?.value?.toLowerCase() === 'true')
        setLoaded(true)
      })
      .catch(() => {
        if (!active) return
        setLoaded(true)
      })
    return () => { active = false }
  }, [])

  return { useIndex, loaded }
}

export default useBomLibraryIndexSetting
